import { useState } from 'react';
import { X, Bell, Users, ArrowRightLeft, FileText, Rocket, CheckCircle, Bot, Wifi } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNotificacoesBKO } from '@/hooks/useNotificacoesBKO';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

const iconesPorTipo: Record<string, React.ReactNode> = {
  leads_recebidos: <Users className="h-6 w-6" />,
  leads_transferidos: <ArrowRightLeft className="h-6 w-6" />,
  novo_pedido_contrato: <FileText className="h-6 w-6" />,
  novo_pedido_input: <Rocket className="h-6 w-6" />,
  pedido_transferido: <CheckCircle className="h-6 w-6" />,
  lead_ia_novo: <Wifi className="h-6 w-6" />,
  lead_ia_atualizado: <Bot className="h-6 w-6" />,
};

const coresPorPrioridade: Record<string, { bg: string; border: string; icon: string }> = {
  urgente: { bg: 'bg-danger/5 dark:bg-danger/40', border: 'border-danger', icon: 'text-danger dark:text-danger' },
  alta: { bg: 'bg-warning/5 dark:bg-warning/40', border: 'border-warning', icon: 'text-warning dark:text-warning' },
  normal: { bg: 'bg-info/5 dark:bg-info/40', border: 'border-info', icon: 'text-info dark:text-info' },
  baixa: { bg: 'bg-muted', border: 'border-muted-foreground/30', icon: 'text-muted-foreground' },
};

export function NotificacaoLateral() {
  const { notificacoesLaterais, dispensarNotificacaoLateral } = useNotificacoesBKO();

  // Show only the most recent one
  const notif = notificacoesLaterais[0];

  if (!notif) return null;

  const cores = coresPorPrioridade[notif.prioridade] || coresPorPrioridade.normal;
  const icone = iconesPorTipo[notif.tipo] || <Bell className="h-6 w-6" />;

  return (
    <AnimatePresence>
      <motion.div
        key={notif.id}
        initial={{ x: 400, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 400, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className={cn(
          'fixed top-20 right-4 z-50 w-[380px] max-w-[calc(100vw-2rem)] rounded-xl border-l-4 shadow-2xl',
          cores.bg,
          cores.border,
        )}
      >
        {/* Header */}
        <div className="flex items-start gap-3 p-4 pb-2">
          <div className={cn('mt-0.5 flex-shrink-0', cores.icon)}>
            {icone}
          </div>
          <h4 className="font-bold text-base text-foreground flex-1 leading-tight">
            {notif.titulo}
          </h4>
          <button
            onClick={() => dispensarNotificacaoLateral(notif.id)}
            className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 pb-2">
          <p className="text-sm text-foreground/80 leading-relaxed">
            {notif.mensagem}
          </p>
        </div>

        {/* Footer */}
        <div className="px-4 pb-4 pt-2 flex items-center justify-between">
          {notificacoesLaterais.length > 1 && (
            <span className="text-xs text-muted-foreground">
              +{notificacoesLaterais.length - 1} outra{notificacoesLaterais.length - 1 > 1 ? 's' : ''}
            </span>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => dispensarNotificacaoLateral(notif.id)}
            className="ml-auto"
          >
            Entendi
          </Button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
